/**
 * Keeping a transport's network path alive, when the event that says it died
 * only ever fires once.
 *
 * The recovery this replaces was a single `connectionstatechange` handler:
 *
 *   if (state === 'disconnected' || state === 'failed') {
 *     try { ...restartIce... } catch { /* non-fatal *\/ }
 *   }
 *
 * Three things were wrong with it, and together they made a failed send
 * transport permanent for the rest of the call:
 *
 *  1. It is EDGE-TRIGGERED, and mediasoup-client suppresses a repeat of the
 *     same state (`Transport.js:746`: `if (connectionState === this._connectionState) return`).
 *     So once a transport settles in `failed`, the handler can never run again —
 *     one attempt, ever.
 *  2. The single attempt rode `control.restartIce`, which has no retry wrapper
 *     and a 15s request timeout. Under load that request is exactly what times
 *     out, so the one attempt was also the one most likely to be lost.
 *  3. Its failure went to `catch {}` — no log, no counter. A publisher whose
 *     video reached nobody for 37 minutes produced not one line of evidence.
 *
 * Measured: an observer's send transport sat at `iceConnectionState:
 * "disconnected"`, `connectionState: "failed"`, with NO selected candidate pair,
 * across probes minutes apart, while its encoder happily produced 80x45 frames
 * that went nowhere.
 *
 * So this is level-triggered, the same shape as the SFU's 30s layer-trim
 * re-assert: ask the transport whether it currently has a usable path, and
 * while it does not, keep trying. A condition that is checked cannot be missed
 * the way an event can.
 */

export type WatchdogTransport = {
  id: string
  closed: boolean
  /**
   * mediasoup-client's transport state. `'new'` means the transport has never
   * been used: it connects lazily, on its first produce or consume.
   */
  connectionState?: string
  getStats(): Promise<Map<string, Record<string, unknown>> | Iterable<[string, Record<string, unknown>]>>
  restartIce(params: { iceParameters: unknown }): Promise<void>
}

/** Why the watchdog gave up. Both mean "restartIce cannot fix this". */
export type Unrecoverable =
  /** The SFU no longer has this transport — it was closed server-side. */
  | 'transport-gone'
  /** Repeated restarts all failed; the path is not coming back on its own. */
  | 'restart-exhausted'

/**
 * Does this transport currently have a usable network path?
 *
 * The honest signal is a candidate pair in state `succeeded`, not the
 * connection state enum — a transport can read `disconnected` while a pair is
 * still nominated and recovering, and (the case that bit us) can read `failed`
 * with no pair at all while `outbound-rtp` keeps incrementing, because the
 * encoder does not know the packets are going nowhere.
 *
 * A nominated pair counts only while it has not FAILED. `nominated` is never
 * cleared, so a network change — Wi-Fi to another access point, a new IP —
 * leaves the old pair nominated and failed for good, and counting it as live
 * meant the watchdog never restarted ICE: media stayed dead on a call whose
 * sockets had all reconnected. For the same reason the browser's own verdict
 * wins when it has one: a transport whose `iceState` reads `failed`, or whose
 * selected pair has failed, has no path whatever else the report says.
 */
export async function hasLivePath(transport: WatchdogTransport): Promise<boolean> {
  let stats
  try {
    stats = await transport.getStats()
  } catch {
    // A transport that cannot be asked is not evidence of health.
    return false
  }
  // RTCStatsReport is a Map; iterate values whichever shape we were handed.
  const entries = stats instanceof Map ? stats.values() : [...(stats as Iterable<[string, Record<string, unknown>]>)].map((e) => e[1])
  const pairs: Record<string, unknown>[] = []
  const selectedIds: unknown[] = []
  for (const s of entries as Iterable<Record<string, unknown>>) {
    if (!s) continue
    if (s.type === 'transport') {
      if (s.iceState === 'failed') return false
      if (s.selectedCandidatePairId) selectedIds.push(s.selectedCandidatePairId)
    } else if (s.type === 'candidate-pair') {
      pairs.push(s)
    }
  }
  const usable = (p: Record<string, unknown>) =>
    p.state === 'succeeded' || (p.nominated === true && p.state !== 'failed')
  // The browser names the pair it is using: judge that one, not a stray
  // succeeded pair on another interface.
  const selected = pairs.filter((p) => p.id !== undefined && selectedIds.includes(p.id))
  if (selected.length > 0) return selected.some(usable)
  return pairs.some(usable)
}

export type WatchdogOptions = {
  transport: WatchdogTransport
  /** Ask the SFU for fresh ICE parameters. Rejects if the SFU has closed it. */
  requestIceParameters: (transportId: string) => Promise<{ iceParameters: unknown }>
  /** Called for every attempt and every failure — this path must never be silent. */
  onLog: (event: string, detail: Record<string, unknown>) => void
  /** The path cannot be restored; the caller should rebuild. */
  onUnrecoverable: (reason: Unrecoverable) => void
  /** How often to check. */
  intervalMs?: number
  /** Consecutive failed restarts before declaring it unrecoverable. */
  maxAttempts?: number
  setTimeoutFn?: (fn: () => void, ms: number) => unknown
  clearTimeoutFn?: (handle: unknown) => void
  /**
   * Whether the browser has a network at all. While it does not, a restart
   * can only fail, and its failure says nothing about this path — so the
   * watchdog waits rather than spend its attempts, and the browser's `online`
   * kicks a check the moment the network is back. Measured 2026-10-13: a 70 s
   * Wi-Fi drop spent all four attempts on the outage itself, 15 s apiece, and
   * the session was rebuilt for it. Defaults to `navigator.onLine`; anything
   * but an explicit `false` counts as online.
   */
  isOnline?: () => boolean
}

const browserOnline = (): boolean => typeof navigator === 'undefined' || navigator.onLine !== false

/**
 * A running watchdog. Call it to stop it.
 *
 * `kick` checks now instead of at the next tick — for when something else has
 * just said the path may have changed, such as the control socket coming back
 * after an outage. A kick that arrives while a check is already under way
 * does not start a second one alongside it: that check is followed by
 * another at once. Two restarts for one transport would race their
 * credentials — the server rotates them on every call — and whichever the
 * browser applied last might not be the one the server kept.
 */
export type IceWatchdog = (() => void) & {
  /**
   * `resetFailures`: the attempts so far belonged to an outage that is now
   * over (the browser is back online), so the check starts a fresh run
   * rather than being one more failure on the way to giving up.
   */
  kick: (reason: string, opts?: { resetFailures?: boolean }) => void
}

/** A rejection that means the SFU has already closed this transport. */
export function isTransportGone(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '')
  return /transport .*not found|not found/i.test(msg)
}

/**
 * Start watching. Returns a stop function.
 *
 * Deliberately does NOT stop on success: a path that came back can go again,
 * and the whole point is that this survives more than one failure.
 */
export function createIceWatchdog(opts: WatchdogOptions): IceWatchdog {
  const intervalMs = opts.intervalMs ?? 5_000
  const maxAttempts = opts.maxAttempts ?? 4
  const setT = opts.setTimeoutFn ?? ((fn, ms) => setTimeout(fn, ms))
  const clearT = opts.clearTimeoutFn ?? ((h) => clearTimeout(h as ReturnType<typeof setTimeout>))

  let stopped = false
  let handle: unknown = null
  let consecutiveFailures = 0
  let announced = false
  /** A check is under way (it awaits stats, then possibly a restart). */
  let inFlight = false
  /** Kicked while a check was under way: check again as soon as it ends. */
  let kickPending = false
  const isOnline = opts.isOnline ?? browserOnline
  /** Paused for want of a network, said once per outage. */
  let pausedOffline = false

  const schedule = (delay: number) => {
    if (stopped || opts.transport.closed) return
    handle = setT(() => { handle = null; void tick() }, delay)
  }

  const giveUp = (reason: Unrecoverable) => {
    if (announced) return
    announced = true
    opts.onLog('ice recovery gave up', { transportId: opts.transport.id, reason })
    opts.onUnrecoverable(reason)
  }

  const tick = async () => {
    if (stopped || opts.transport.closed) return
    if (!isOnline()) {
      if (!pausedOffline) {
        pausedOffline = true
        opts.onLog('offline — checks paused', { transportId: opts.transport.id, failures: consecutiveFailures })
      }
      schedule(intervalMs)
      return
    }
    pausedOffline = false
    inFlight = true
    try {
      // A transport nobody has used yet has no path because it has never asked
      // for one, not because it lost it. mediasoup-client connects a transport
      // on its first produce/consume, so a recv transport with no consumer yet
      // (or a send transport with camera and mic off) sits in 'new' with no
      // candidate pair, correctly. Judging it dead made the watchdog restart ICE
      // on it, find it unrecoverable and rebuild the session — which, when the
      // consumers had been lost in the first place, only repeated the loss,
      // three times over, until MAX_REJOINS gave up (2026-09-28).
      if (opts.transport.connectionState === 'new') {
        consecutiveFailures = 0
        return
      }
      if (await hasLivePath(opts.transport)) {
        // Healthy. Forget past failures — this is a fresh episode if it recurs.
        if (consecutiveFailures > 0) {
          opts.onLog('ice path restored', { transportId: opts.transport.id, afterAttempts: consecutiveFailures })
        }
        consecutiveFailures = 0
        announced = false
        return
      }

      consecutiveFailures += 1
      opts.onLog('ice path missing — restarting', {
        transportId: opts.transport.id, attempt: consecutiveFailures,
      })

      try {
        const { iceParameters } = await opts.requestIceParameters(opts.transport.id)
        if (stopped || opts.transport.closed) return
        await opts.transport.restartIce({ iceParameters })
        opts.onLog('ice restart issued', { transportId: opts.transport.id, attempt: consecutiveFailures })
      } catch (err) {
        // The SFU closing a transport is terminal: no number of restarts brings
        // back a transport the server has forgotten. Rebuilding is the only move.
        if (isTransportGone(err)) {
          opts.onLog('ice restart refused — SFU has closed this transport', {
            transportId: opts.transport.id, error: (err as Error)?.message,
          })
          giveUp('transport-gone')
          // And stop. The server has forgotten this transport; asking again
          // every few seconds only fills both logs — it did, every ~21 s, for as
          // long as the page stayed open. The session rebuild that giveUp
          // triggers brings its own transports and its own watchdogs.
          stopped = true
          return
        }
        if (!isOnline()) {
          // The network went while this attempt was out: not this path's
          // failure, and not one to count toward giving up on it.
          consecutiveFailures -= 1
          opts.onLog('ice restart failed while offline — not counted', {
            transportId: opts.transport.id, error: (err as Error)?.message ?? String(err),
          })
          return
        }
        opts.onLog('ice restart FAILED', {
          transportId: opts.transport.id, attempt: consecutiveFailures,
          error: (err as Error)?.message ?? String(err),
        })
      }

      if (consecutiveFailures >= maxAttempts) giveUp('restart-exhausted')
    } finally {
      inFlight = false
      if (kickPending) {
        kickPending = false
        schedule(0)
      } else {
        // Back off as failures accumulate, so a genuinely dead path does not
        // spend the control plane that everything else needs.
        schedule(intervalMs * Math.min(1 + consecutiveFailures, 4))
      }
    }
  }

  const kick: IceWatchdog['kick'] = (reason, kickOpts = {}) => {
    if (stopped || opts.transport.closed) return
    if (kickOpts.resetFailures) consecutiveFailures = 0
    opts.onLog('checking now', { transportId: opts.transport.id, reason })
    if (inFlight) {
      kickPending = true
      return
    }
    if (handle != null) clearT(handle)
    handle = null
    schedule(0)
  }

  schedule(intervalMs)

  const stop = () => {
    stopped = true
    if (handle != null) clearT(handle)
    handle = null
  }
  return Object.assign(stop, { kick })
}
